import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { FieldErrorText, FieldHelperText, FieldLabel, useFieldChrome } from '../field/field-message.js';
export function Combobox({ id, label, options, value, onValueChange, onQueryChange, placeholder, disabled, errorText, helperText, required, loading, serverFiltered = false, }) {
    const { helperId, errorId, describedBy } = useFieldChrome({ id, helperText, errorText });
    const hasError = Boolean(errorText);
    const selected = options.find((o) => o.value === value);
    const [open, setOpen] = React.useState(false);
    const [query, setQuery] = React.useState(selected?.label ?? '');
    const [activeIndex, setActiveIndex] = React.useState(-1);
    const listboxId = `${id}-listbox`;
    const containerRef = React.useRef(null);
    React.useEffect(() => {
        setQuery(selected?.label ?? '');
    }, [selected?.label]);
    React.useEffect(() => {
        if (!open)
            return undefined;
        const handlePointerDown = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [open]);
    const filtered = serverFiltered
        ? options
        : options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()));
    const commit = (option) => {
        onValueChange(option.value);
        setQuery(option.label);
        setOpen(false);
        setActiveIndex(-1);
    };
    const handleKeyDown = (event) => {
        if (disabled)
            return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setOpen(true);
            setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
        }
        else if (event.key === 'Enter') {
            if (open && activeIndex >= 0 && filtered[activeIndex]) {
                event.preventDefault();
                commit(filtered[activeIndex]);
            }
        }
        else if (event.key === 'Escape') {
            setOpen(false);
            setActiveIndex(-1);
        }
    };
    return (_jsxs("div", { className: "w-full min-w-0", ref: containerRef, children: [_jsx(FieldLabel, { htmlFor: id, required: required, children: label }), _jsxs("div", { className: "relative", children: [_jsx("input", { id: id, role: "combobox", "aria-expanded": open, "aria-controls": listboxId, "aria-autocomplete": "list", "aria-invalid": hasError || undefined, "aria-describedby": describedBy, "aria-activedescendant": activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined, disabled: disabled, placeholder: placeholder, value: query, onChange: (event) => {
                            setQuery(event.target.value);
                            setOpen(true);
                            setActiveIndex(-1);
                            onQueryChange?.(event.target.value);
                        }, onFocus: () => setOpen(true), onKeyDown: handleKeyDown, className: cn('block w-full min-w-0 rounded-sm border bg-surface-base px-3 py-2 text-base text-text-primary', 'placeholder:text-text-muted transition-colors duration-fast', 'focus-visible:outline-none focus-visible:shadow-focus', hasError ? 'border-status-critical-solid' : 'border-border-functional hover:border-neutral-700', disabled && 'cursor-not-allowed border-neutral-200 bg-neutral-100 text-text-disabled') }), open && !disabled ? (_jsx("ul", { id: listboxId, role: "listbox", "aria-label": label, className: "absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-auto rounded-md border border-border-functional bg-surface-elevated p-1 shadow-elevation-2", children: loading ? (_jsx("li", { className: "space-y-1 p-2", "aria-hidden": "true", children: [0, 1, 2].map((i) => (_jsx("div", { className: "h-8 animate-pulse rounded-sm bg-neutral-100 motion-reduce:animate-none" }, i))) })) : filtered.length === 0 ? (_jsx("li", { role: "status", className: "px-3 py-4 text-center text-sm text-text-muted", children: "No matches \u2014 try a different search term" })) : (filtered.map((option, index) => (_jsx("li", { id: `${id}-option-${index}`, role: "option", "aria-selected": option.value === value, onMouseDown: (event) => {
                                event.preventDefault();
                                commit(option);
                            }, onMouseEnter: () => setActiveIndex(index), className: cn('cursor-pointer select-none rounded-sm px-3 py-2 text-sm text-text-primary', index === activeIndex && 'bg-surface-hover', option.value === value && 'bg-surface-selected'), children: option.label }, option.value)))) })) : null] }), helperText && !hasError ? _jsx(FieldHelperText, { id: helperId, children: helperText }) : null, hasError ? _jsx(FieldErrorText, { id: errorId, children: errorText }) : null] }));
}
//# sourceMappingURL=Combobox.js.map